import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  loadGoogleMapsScript,
  getUserLocation,
  getDefaultLocation,
  geocodeAddress,
  calculateDistance,
  formatDistance,
  showNavigationPicker,
} from '../utils/mapUtils';

/**
 * EventMap — Google Maps embed showing event markers
 *
 * Props:
 *  events: [{ id, name, venue, venueCoordinates?: {lat, lng}, date }]
 *  singleEvent: if true, show single event pin + info (no clustering)
 *  onEventClick: (event) => void
 *  height: CSS height (default 400px)
 */
export default function EventMap({ events = [], singleEvent = false, onEventClick, height = '400px' }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef([]);
  const [userLocation, setUserLocation] = useState(null);
  const [locationPrompt, setLocationPrompt] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navPicker, setNavPicker] = useState(null);

  // Get user location with fallback prompt
  const initLocation = useCallback(async () => {
    const loc = await getUserLocation();
    if (loc) {
      setUserLocation(loc);
    } else {
      // GPS denied — show prompt
      setLocationPrompt(true);
    }
  }, []);

  const useDefaultLocation = useCallback(() => {
    setUserLocation(getDefaultLocation());
    setLocationPrompt(false);
  }, []);

  const retryPermission = useCallback(async () => {
    setLocationPrompt(false);
    const loc = await getUserLocation();
    if (loc) {
      setUserLocation(loc);
    } else {
      setLocationPrompt(true);
    }
  }, []);

  // Geocode events without coordinates
  const resolveCoordinates = useCallback(async (evts) => {
    const resolved = [];
    for (const e of evts) {
      if (e.venueCoordinates?.lat && e.venueCoordinates?.lng) {
        resolved.push(e);
      } else if (e.venue) {
        const coords = await geocodeAddress(e.venue);
        if (coords) {
          resolved.push({ ...e, venueCoordinates: coords });
        }
      }
    }
    return resolved;
  }, []);

  // Initialize map
  useEffect(() => {
    let cancelled = false;

    async function init() {
      try {
        setLoading(true);
        await loadGoogleMapsScript();
        await initLocation();

        const eventsWithCoords = await resolveCoordinates(events);

        if (cancelled || !mapRef.current) return;

        // Determine center
        const center = userLocation
          || (eventsWithCoords[0]?.venueCoordinates)
          || getDefaultLocation();

        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: center.lat, lng: center.lng },
          zoom: singleEvent ? 15 : 11,
          styles: darkMapStyle,
          disableDefaultUI: false,
          zoomControl: true,
          fullscreenControl: true,
          streetViewControl: false,
          mapTypeControl: false,
        });
        mapInstanceRef.current = map;

        // Clear old markers
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];

        // User location marker (blue dot)
        if (userLocation || center) {
          const userPos = userLocation || center;
          new window.google.maps.Marker({
            position: { lat: userPos.lat, lng: userPos.lng },
            map,
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: '#4285F4',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 2,
            },
            title: userPos.isDefault ? 'Default Location (Palm Beach)' : 'Your Location',
            zIndex: 1000,
          });
        }

        // Event markers
        const bounds = new window.google.maps.LatLngBounds();
        if (userLocation) bounds.extend({ lat: userLocation.lat, lng: userLocation.lng });

        eventsWithCoords.forEach((event) => {
          const pos = { lat: event.venueCoordinates.lat, lng: event.venueCoordinates.lng };
          bounds.extend(pos);

          const marker = new window.google.maps.Marker({
            position: pos,
            map,
            title: event.name || event.title,
            icon: {
              path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
              fillColor: '#6c63ff',
              fillOpacity: 1,
              strokeColor: '#fff',
              strokeWeight: 1,
              scale: 1.5,
              anchor: new window.google.maps.Point(12, 24),
            },
          });

          const dist = userLocation
            ? formatDistance(calculateDistance(userLocation.lat, userLocation.lng, pos.lat, pos.lng))
            : '';

          const infoContent = `
            <div style="font-family:sans-serif;min-width:200px;color:#333">
              <strong style="font-size:14px">${event.name || event.title || 'Event'}</strong>
              <p style="margin:4px 0;font-size:12px;color:#666">${event.venue || ''}</p>
              ${event.date ? `<p style="margin:4px 0;font-size:12px;color:#666">📅 ${new Date(event.date).toLocaleDateString()}</p>` : ''}
              ${dist ? `<p style="margin:4px 0;font-size:12px;color:#4285F4">📍 ${dist} away</p>` : ''}
              <div style="margin-top:8px;display:flex;gap:6px">
                <button onclick="window.__mapNavEvent=${JSON.stringify({ lat: pos.lat, lng: pos.lng, label: event.venue }).replace(/"/g, '&quot;')}" style="padding:4px 10px;background:#6c63ff;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:12px">Navigate</button>
              </div>
            </div>
          `;

          const infoWindow = new window.google.maps.InfoWindow({ content: infoContent });
          marker.addListener('click', () => {
            infoWindow.open(map, marker);
            if (onEventClick) onEventClick(event);
          });

          markersRef.current.push(marker);
        });

        if (eventsWithCoords.length > 0 && !singleEvent) {
          map.fitBounds(bounds, 50);
        }

        setLoading(false);
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(e.message);
          setLoading(false);
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [events, singleEvent, userLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for navigation clicks from info windows
  useEffect(() => {
    const handler = () => {
      const data = window.__mapNavEvent;
      if (data) {
        try {
          const parsed = typeof data === 'string' ? JSON.parse(data) : data;
          setNavPicker(parsed);
        } catch (e) { /* ignore */ }
        window.__mapNavEvent = null;
      }
    };
    window.addEventListener('click', handler);
    return () => window.removeEventListener('click', handler);
  }, []);

  // Location permission prompt
  if (locationPrompt) {
    return (
      <div style={styles.prompt}>
        <div style={styles.promptIcon}>📍</div>
        <h3 style={styles.promptTitle}>Location Access</h3>
        <p style={styles.promptText}>
          Location permission was denied. Would you like to use the default location
          (Palm Beach Convention Center) or grant permission?
        </p>
        <div style={styles.promptActions}>
          <button style={styles.btnDefault} onClick={useDefaultLocation}>Use Default Location</button>
          <button style={styles.btnGrant} onClick={retryPermission}>Grant Permission</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      {loading && (
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <span>Loading map...</span>
        </div>
      )}
      {error && (
        <div style={styles.error}>
          <p>⚠️ {error}</p>
          <small>Set REACT_APP_GOOGLE_MAPS_API_KEY in .env</small>
        </div>
      )}
      <div ref={mapRef} style={{ width: '100%', height, borderRadius: 12, overflow: 'hidden' }} />

      {/* Navigation picker modal */}
      {navPicker && (
        <div style={styles.navOverlay} onClick={() => setNavPicker(null)}>
          <div style={styles.navModal} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: '#fff', marginBottom: 12 }}>Navigate to Venue</h3>
            {showNavigationPicker(navPicker.lat, navPicker.lng, navPicker.label).map(opt => (
              <a key={opt.label} href={opt.url} target="_blank" rel="noopener noreferrer" style={styles.navOption} onClick={() => setNavPicker(null)}>
                <span>{opt.icon}</span> {opt.label}
              </a>
            ))}
            <button style={styles.navCancel} onClick={() => setNavPicker(null)}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  prompt: { background: '#1a1a2e', borderRadius: 12, padding: 32, textAlign: 'center', border: '1px solid #2a2a3e' },
  promptIcon: { fontSize: 48, marginBottom: 12 },
  promptTitle: { color: '#fff', marginBottom: 8 },
  promptText: { color: '#888', fontSize: 14, lineHeight: 1.5, marginBottom: 20, maxWidth: 400, margin: '0 auto 20px' },
  promptActions: { display: 'flex', gap: 12, justifyContent: 'center' },
  btnDefault: { padding: '10px 20px', background: '#2a2a3e', border: 'none', borderRadius: 8, color: '#ccc', cursor: 'pointer', fontSize: 14 },
  btnGrant: { padding: '10px 20px', background: 'linear-gradient(135deg, #6c63ff, #4834d4)', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  loading: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(10,10,15,0.8)', borderRadius: 12, zIndex: 10, color: '#888', gap: 10 },
  spinner: { width: 24, height: 24, border: '3px solid #333', borderTopColor: '#6c63ff', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  error: { background: '#2d1b1b', borderRadius: 12, padding: 20, textAlign: 'center', color: '#ff6b6b', marginBottom: 12 },
  navOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 },
  navModal: { background: '#1a1a2e', borderRadius: 16, padding: 24, minWidth: 280, textAlign: 'center' },
  navOption: { display: 'block', padding: '12px 16px', margin: '8px 0', background: '#2a2a3e', borderRadius: 8, color: '#e0e0e0', textDecoration: 'none', fontSize: 15 },
  navCancel: { marginTop: 12, padding: '10px 24px', background: 'transparent', border: '1px solid #444', borderRadius: 8, color: '#888', cursor: 'pointer', width: '100%' },
};

// Dark map style
const darkMapStyle = [
  { elementType: 'geometry', stylers: [{ color: '#1d1d35' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1a2e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8a8a9e' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2a2a4a' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#1a1a3a' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0e0e2a' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#252545' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a2e1a' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2a2a4a' }] },
];
